/* eslint-disable no-console */
import StorageService from "@/services/storage.service";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";

const TokenService = {
  debug: false,
  accessToken: null,
  accessTokenExpired: false,
  accessTokenExpireTimeout: null,
  accessTokenCookieName: "olaii_access_token",

  refreshToken: null,
  refreshTokenExpired: false,
  refreshTokenExpireTimeout: null,
  refreshTokenCookieName: "olaii_refresh_token",
  refreshingTokenPromise: null,

  getHeader() {
    if (this.accessToken) {
      return {
        Authorization: `Bearer ${this.accessToken}`,
        "Content-Type": "application/json",
      };
    } else {
      return {
        "Content-Type": "application/json",
      };
    }
  },

  readFromCookies() {
    this.getAccessToken();
    this.getRefreshToken();

    if (!this.accessToken) {
      if (this.debug) console.log("[TOKEN] No token saved :(");
    }
  },

  hasToken() {
    return this.accessToken != null;
  },

  getAccessToken() {
    this.accessToken = StorageService.get(this.accessTokenCookieName);
    if (this.accessToken) {
      const access_token_decoded = jwtDecode(this.accessToken);
      const access_token_expire_date = new Date(access_token_decoded.exp * 1000);
      let access_token_expire_milliseconds = access_token_expire_date - new Date();

      if (this.debug) {
        console.log("[TOKEN] Access token", {
          decoded: access_token_decoded,
          expire_date: access_token_expire_date.toString(),
          expire_date_milliseconds: access_token_expire_milliseconds,
          // time_left: formatDistanceToNow(access_token_expire_date),
        });
      }

      if (access_token_expire_milliseconds <= 0) {
        if (this.debug) console.error("[TOKEN] Access token expired!");
        this.accessTokenExpired = true;
      } else {
        this.accessTokenExpired = false;
      }

      clearTimeout(this.accessTokenExpireTimeout);
      if (access_token_expire_milliseconds > 2147483600) access_token_expire_milliseconds = 2147483600;
      if (access_token_expire_milliseconds >= 0) {
        this.accessTokenExpireTimeout = setTimeout(() => {
          if (this.debug) console.error("[TOKEN] Access token timeout triggered!");
          // this.accessTokenExpired = true;
          TokenService.getAccessToken();
        }, access_token_expire_milliseconds);
      }
    }
    return this.accessToken;
  },

  setAccessToken(accessToken) {
    this.accessToken = accessToken;
    StorageService.save(this.accessTokenCookieName, this.accessToken);
    this.getAccessToken();
  },

  removeAccessToken() {
    this.accessToken = null;
    StorageService.remove(this.accessTokenCookieName);
    clearTimeout(this.accessTokenExpireTimeout);
  },

  getRefreshToken() {
    this.refreshToken = StorageService.get(this.refreshTokenCookieName);
    if (this.refreshToken) {
      const refresh_token_decoded = jwtDecode(this.refreshToken);
      const refresh_token_expire_date = new Date(refresh_token_decoded.exp * 1000);
      let refresh_token_expire_milliseconds = refresh_token_expire_date - new Date();

      if (this.debug) {
        console.log("[TOKEN] Refresh token", {
          decoded: refresh_token_decoded,
          expire_date: refresh_token_expire_date.toString(),
          expire_date_milliseconds: refresh_token_expire_milliseconds,
          // time_left: formatDistanceToNow(refresh_token_expire_date),
        });
      }

      if (refresh_token_expire_milliseconds <= 0) {
        if (this.debug) console.error("[TOKEN] Refresh token expired!");
        this.refreshTokenExpired = true;
      } else {
        this.refreshTokenExpired = false;
      }

      clearTimeout(this.refreshTokenExpireTimeout);
      if (refresh_token_expire_milliseconds > 2147483600) refresh_token_expire_milliseconds = 2147483600;
      if (refresh_token_expire_milliseconds >= 0) {
        this.refreshTokenExpireTimeout = setTimeout(() => {
          if (this.debug) console.error("[TOKEN] Refresh token timeout triggered!");
          // this.refreshTokenExpired = true;
          TokenService.getRefreshToken();
        }, refresh_token_expire_milliseconds);
      }
    }
    return this.refreshToken;
  },

  setRefreshToken(refreshToken) {
    this.refreshToken = refreshToken;
    StorageService.save(this.refreshTokenCookieName, this.refreshToken);
    this.getRefreshToken();
  },

  removeRefreshToken() {
    this.refreshToken = null;
    StorageService.remove(this.refreshTokenCookieName);
    clearTimeout(this.refreshTokenExpireTimeout);
  },

  async refreshAccessToken() {
    if (!TokenService.refreshingTokenPromise) {
      this.getRefreshToken();
      const request = {
        method: "post",
        url: process.env.VUE_APP_AUTH_API + "api/v2/token/refresh/",
        headers: this.getHeader(),
        data: {
          refresh: TokenService.refreshToken,
        },
      };
      TokenService.refreshingTokenPromise = axios(request);
    }
    const response = await TokenService.refreshingTokenPromise;
    TokenService.refreshingTokenPromise = null;
    if (this.debug) console.log("[TOKEN] Token refreshed:", response.data);
    TokenService.setAccessToken(response.data.access);
    TokenService.setRefreshToken(response.data.refresh);
    return response.data.access;
  },

  clear() {
    this.removeAccessToken();
    this.removeRefreshToken();
  },
};

export default TokenService;
